import { create } from "zustand";
import api from "../utilities/api.js";

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  error: null,
  isLoading: false,
  isCheckingAuth: true,
  message: null,
  sessionExpired: false,

  // Helper function to check if user is admin
  isAdmin: () => {
    const { user } = get();
    return user?.role === "admin";
  },

  // Helper function to check if user is member
  isMember: () => {
    const { user } = get();
    return user?.role === "member";
  },

  signup: async (email, password, name) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(`/auth/signup`, {
        email,
        password,
        name,
      });
      set({
        user: response.data.user,
        isAuthenticated: false,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error.response.data.message || "Error signing up",
        isLoading: false,
      });
      throw error;
    }
  },

  login: async (email, password, name) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(`/auth/login`, {
        email,
        password,
        name,
      });
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionExpired: false,
      });
    } catch (error) {
      set({
        error: error.response.data.message || "Error logging in",
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/auth/logout`);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionExpired: false,
      });
    } catch (error) {
      set({
        error: error.response.data.message || "Error logging out",
        isLoading: false,
      });
      throw error;
    }
  },

  verifyEmail: async (code) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(`/auth/verify`, {
        code,
      });
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response.data.message || "Error verifying email",
        isLoading: false,
      });
      throw error;
    }
  },

  forgotPassword: async (email) => {
    set({ isLoading: true, error: null, message: null });
    try {
      const response = await api.post(
        `/auth/forgot-password`,
        {
          email,
        },
      );
      set({
        message: response.data.message,
        isLoading: false,
      });
      return { success: true };
    } catch {
      set({
        isLoading: false,
        // Don't set error - we want to show the same message regardless to prevent email enumeration attacks
        message:
          "If an account with that email exists, a password reset link has been sent",
      });
      // Don't throw - return success anyway for security
      return { success: true };
    }
  },

  resetPassword: async (token, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(
        `/auth/reset-password/${token}`,
        {
          password,
        },
      );
      set({
        message: response.data.message,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response.data.message || "Error resetting password",
        isLoading: false,
      });
      throw error;
    }
  },

  resendVerificationEmail: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(`/auth/resend`, {
        email,
      });
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error:
          error.response.data.message || "Error resending verification email",
        isLoading: false,
      });
      throw error;
    }
  },

  // Marks the session as expired due to inactivity; SessionExpiredWatcher
  // reacts to this to show a toast and redirect to the login page.
  flagSessionExpired: () => {
    set({ user: null, isAuthenticated: false, sessionExpired: true });
  },

  clearSessionExpired: () => {
    set({ sessionExpired: false });
  },

  checkAuth: async () => {
    set({ isCheckingAuth: true, error: null });
    try {
      const response = await api.get(`/auth/check-auth`);

      if (response.data.success) {
        set({
          user: response.data.user,
          isAuthenticated: true,
          isCheckingAuth: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isCheckingAuth: false,
        });
      }
    } catch {
      // console.warn("Authentication check failed:", error.message);
      set({ error: null, isCheckingAuth: false, isAuthenticated: false });
    }
  },
}));
